export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold mb-4">GetBokr.com Terms of Service</h1>
        <p className="text-muted-foreground mb-2">Operated by Lifely Holdings Limited</p>
        <p className="text-muted-foreground mb-2">NZBN: 9429053450883</p>
        <p className="text-muted-foreground mb-2">Company Number: 9405975</p>
        <p className="text-muted-foreground mb-8">Last Updated: February 2026</p>

        <div className="prose prose-neutral dark:prose-invert max-w-none space-y-8">
          <section>
            <h2 className="text-xl font-semibold mb-4">1. Acceptance of Terms</h2>
            <p>By accessing or using GetBokr ("Bokr", "the Service"), you agree to these Terms of Service ("Terms").</p>
            <p>If you do not agree, you must not use the Service.</p>
            <p>These Terms form a legally binding agreement between you and Lifely Holdings Limited, a company incorporated in New Zealand.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">2. Nature of the Service</h2>
            <p>GetBokr is a cloud-based software platform that provides scheduling, invoicing, and business workflow tools.</p>
            <p className="mt-2">We:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Provide software only</li>
              <li>Do not provide legal, accounting, financial, or compliance advice</li>
              <li>Do not act as a payment processor</li>
              <li>Do not act as a data controller for your clients</li>
            </ul>
            <p className="mt-2 font-medium">You are solely responsible for how you use the Service.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">3. Global Use</h2>
            <p>The Service may be accessed globally.</p>
            <p className="mt-2">By using GetBokr, you agree that:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>The Service is operated from New Zealand</li>
              <li>You use it at your own risk</li>
              <li>You are responsible for compliance with local laws in your jurisdiction</li>
            </ul>
            <p className="mt-2">We do not represent that the Service is appropriate or compliant in all countries.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">4. Account Responsibilities</h2>
            <p>You agree to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Provide accurate information</li>
              <li>Maintain the security of your account</li>
              <li>Be responsible for all activity under your login</li>
            </ul>
            <p className="mt-2">We are not liable for unauthorised account access caused by your failure to safeguard credentials.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">5. Customer Data Responsibility</h2>
            <p>You are solely responsible for:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>All data entered into the Service</li>
              <li>The legality of storing that data</li>
              <li>Your compliance with privacy laws (e.g. GDPR, CCPA, etc.)</li>
            </ul>
            <p className="mt-4">We:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Do not monitor user data</li>
              <li>Do not verify accuracy of data</li>
              <li>Are not responsible for data loss, misuse, or legal consequences of stored data</li>
            </ul>
            <p className="mt-2">You warrant that you have the right to store and process all data entered.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">6. No Warranty / Software Provided "As Is"</h2>
            <p>The Service is provided:</p>
            <p className="font-medium">"AS IS" and "AS AVAILABLE"</p>
            <p className="mt-2">We expressly disclaim all warranties, including:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Fitness for a particular purpose</li>
              <li>Merchantability</li>
              <li>Non-infringement</li>
              <li>Accuracy or reliability</li>
              <li>Error-free operation</li>
            </ul>
            <p className="mt-4">We do not guarantee that:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>The software will be bug-free</li>
              <li>The Service will be uninterrupted</li>
              <li>Features will always function as intended</li>
              <li>Data will never be lost</li>
            </ul>
            <p className="mt-2">Software may contain bugs, errors, or unexpected behaviour.</p>
            <p className="font-medium">You use the Service at your own risk.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">7. Force Majeure</h2>
            <p>We are not liable for any failure or delay caused by events beyond our reasonable control, including but not limited to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Natural disasters</li>
              <li>War or civil unrest</li>
              <li>Internet outages</li>
              <li>Cyberattacks</li>
              <li>Government actions</li>
              <li>Infrastructure failure</li>
              <li>Pandemics</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">8. Third-Party Providers</h2>
            <p>The Service relies on third-party infrastructure and integrations, including but not limited to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Cloud hosting providers</li>
              <li>Authentication providers (e.g. Memberstack)</li>
              <li>Edge platforms (e.g. Cloudflare)</li>
              <li>Payment processors (e.g. Stripe)</li>
              <li>Email and SMS providers</li>
            </ul>
            <p className="mt-4">We are not responsible for:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Third-party outages</li>
              <li>Third-party data breaches</li>
              <li>API changes</li>
              <li>Service degradation</li>
              <li>Data loss caused by third parties</li>
            </ul>
            <p className="mt-2">Your use of third-party services is subject to their own terms and policies.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">9. Service Availability</h2>
            <p>We may:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Modify features</li>
              <li>Suspend services</li>
              <li>Perform maintenance</li>
              <li>Remove functionality</li>
            </ul>
            <p className="mt-2">At any time without liability.</p>
            <p>We do not guarantee uptime or service continuity.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">10. Limitation of Liability</h2>
            <p>To the maximum extent permitted by law:</p>
            <p className="mt-2">Lifely Holdings Limited shall not be liable for:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Indirect or consequential damages</li>
              <li>Loss of profits or revenue</li>
              <li>Loss of data</li>
              <li>Business interruption</li>
              <li>Reputational damage</li>
            </ul>
            <p className="mt-4">Our total aggregate liability is limited to the greater of:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Fees paid in the preceding 3 months, or</li>
              <li>NZD $100</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">11. Indemnity</h2>
            <p>You agree to indemnify and hold harmless Lifely Holdings Limited from any claims arising from:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Your use of the Service</li>
              <li>Data you store</li>
              <li>Breach of these Terms</li>
              <li>Violation of laws or third-party rights</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">12. Termination</h2>
            <p>We may suspend or terminate access if:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>You breach these Terms</li>
              <li>There is legal risk</li>
              <li>There is security risk</li>
            </ul>
            <p className="mt-2">You may stop using the Service at any time.</p>
            <p>We may delete data after termination subject to retention policies.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">13. Governing Law & Jurisdiction</h2>
            <p>These Terms are governed exclusively by the laws of New Zealand.</p>
            <p className="mt-2">You agree that:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>New Zealand courts have exclusive jurisdiction</li>
              <li>You waive jurisdiction in other courts to the fullest extent permitted by law</li>
            </ul>
            <p className="mt-2">This applies regardless of where you access the Service.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">14. Changes to Terms</h2>
            <p>We may update these Terms at any time.</p>
            <p>Continued use constitutes acceptance of updated Terms.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">15. Contact</h2>
            <p>Lifely Holdings Limited</p>
            <p>New Zealand</p>
            <p className="font-medium">support@getbokr.com</p>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t">
          <a href="/login" className="text-primary hover:underline">← Back to Login</a>
        </div>
      </div>
    </div>
  );
}
